import { CheckCircle2, Loader2, ArrowUpRight } from "lucide-react";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { BASE_CURRENCY, formatMoney } from "@/lib/money";

// One plan tile — used on the tester Billing page (upgrade/downgrade) and on the
// post-signup plan picker. Kept in one place so the two can never drift apart:
// before this, a change to the card design only reached whichever page someone
// remembered to edit.
//
// `currentPlanId` is optional — a brand-new signup has no current plan, and the
// card simply renders with no badge and every plan selectable.
//
// `onContactUs` is also optional: when a caller supplies it (the Billing page),
// clicking Custom opens an in-app contact form instead of a bare `mailto:` link
// (#85) — a link that silently does nothing on a machine with no mail client
// configured is indistinguishable from a broken button. Signup's picker doesn't
// pass it, so it keeps the plain mailto it always had.
//
// `billingPeriod` ("monthly" | "yearly", #89/#90) only changes what price this
// card SHOWS — which period actually gets charged is decided by the caller
// (create-order's request body), not by anything in here.
//
// `couponPreview` is optional and comes STRAIGHT FROM THE SERVER — the caller
// passes back what POST /billing/validate-coupon returned for THIS plan
// ({ code, discount, final, currency }), never a figure it worked out itself.
// Omitted (the Billing page passes nothing) the card renders exactly as before.
// It is a preview only: create-order re-validates the same code and recomputes
// the charge server-side, so editing this number in a browser changes nothing
// about what is billed.
export default function PlanCard({ plan, currentPlanId, onSelect, onContactUs, busyPlanId, highlight, billingPeriod = "monthly", couponPreview = null }) {
  const isCurrent = currentPlanId != null && plan.id === currentPlanId;
  // Read the EFFECTIVE price — already resolved for this viewer's country by the
  // API — never the INR list price. A company on a USD override was otherwise
  // shown the rupee figure on the card and charged the dollar one at checkout.
  // Falls back through `price` to `price_inr` so an older payload, and the
  // bundled offline catalog, still render.
  const monthly = plan.effectiveMonthly ?? plan.price ?? plan.price_inr;
  const yearlyPrice = plan.effectiveYearly ?? plan.price_yearly ?? plan.price_inr_yearly;
  // A null/undefined price is "Custom — contact us"; 0 is genuinely free.
  // Comparing with `=== 0` treats both as free and offers Enterprise a
  // checkout button that could only ever fail server-side. Keyed off the
  // RESOLVED price above, so a country override cannot turn Custom into a
  // priced plan or vice versa.
  const isCustom = monthly === null || monthly === undefined;
  const isFree = !isCustom && Number(monthly) === 0;
  const isYearly = billingPeriod === "yearly";
  // A plan may carry a monthly price and NO yearly one — the admin catalog
  // explicitly allows the yearly price to be null. formatPrice(null) is the
  // string "Custom", and the period suffix was decided from the MONTHLY price,
  // so such a plan rendered "Custom /year" over a live Subscribe button that
  // could only fail at checkout. Custom itself is excluded: it has no yearly
  // price by definition and must keep its Contact-us button.
  const yearlyUnavailable =
    isYearly && !isCustom && (yearlyPrice === null || yearlyPrice === undefined);
  const showsPeriodSuffix = !isCustom && !isFree && !yearlyUnavailable;
  const displayPrice = yearlyUnavailable
    ? "—"
    : isYearly ? plan.priceDisplayYearly : plan.priceDisplay;
  // Re-entering Free Trial once ANY other plan is current isn't a real,
  // supported action — the backend flatly refuses a $0 order regardless (#87).
  // The button used to stay live and silently do nothing when clicked; better
  // to say so than to look actionable and not be.
  const freeTrialUnavailable = isFree && currentPlanId != null && !isCurrent;
  const isBusy = busyPlanId === plan.id;      // only THIS card spins
  const anyBusy = !!busyPlanId;               // others just disable, so a second
                                               // click during checkout can't fire
                                               // a second Razorpay order for a
                                               // different plan.

  return (
    <div className={cn(
      "card flex flex-col gap-4 p-5 transition-all",
      (plan.featured || highlight) && "ring-2 ring-brand-500",
      isCurrent && "border-emerald-400 dark:border-emerald-500/50"
    )}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[0.6875rem] font-bold uppercase tracking-widest text-slate-400">{plan.label}</span>
        {isCurrent ? (
          <span className="flex shrink-0 items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[0.625rem] font-bold text-emerald-600 dark:text-emerald-300">
            <CheckCircle2 size={10} /> Current Plan
          </span>
        ) : highlight ? (
          <span className="shrink-0 rounded-full bg-brand-500/15 px-2 py-0.5 text-[0.625rem] font-bold text-brand-600 dark:text-brand-300">
            Recommended
          </span>
        ) : null}
      </div>

      <div>
        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{plan.name}</p>
        <p className="mt-1 flex items-end gap-0.5">
          <span className="text-3xl font-bold text-slate-900 dark:text-white">{displayPrice}</span>
          {showsPeriodSuffix && <span className="mb-1 text-sm text-slate-400">{isYearly ? "/year" : "/month"}</span>}
        </p>
        {/* Yearly's flat 20% off has no visible effect unless the card also
            says so — a bigger number with no context reads as MORE
            expensive, not a better deal (#89/#90). */}
        {isYearly && showsPeriodSuffix && plan.yearlySavingsPercent > 0 && (
          <p className="mt-0.5 flex items-center gap-1.5 text-xs">
            <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 font-bold text-emerald-600 dark:text-emerald-300">
              Save {plan.yearlySavingsPercent}%
            </span>
            {/* In the plan's OWN currency — a hardcoded ₹ here sat beside a
                price that may be in dollars or yen. */}
            <span className="text-slate-400">
              ≈ {plan.yearlyMonthlyEquivalentDisplay
                  || formatMoney(plan.yearlyMonthlyEquivalent, plan.currency)}/mo
            </span>
          </p>
        )}
        {yearlyUnavailable && (
          <p className="mt-0.5 text-xs font-medium text-amber-600 dark:text-amber-400">
            Not sold yearly — switch to Monthly to subscribe.
          </p>
        )}
        {/* What this plan actually costs once the entered coupon is applied.
            Rendered under the list price rather than replacing it, so the buyer
            can see the discount rather than just a smaller number. Only shown
            when the server said this code reduces THIS plan's charge. */}
        {couponPreview && couponPreview.discount > 0 && !yearlyUnavailable && (
          <p className="mt-1.5 flex flex-wrap items-baseline gap-1.5 text-xs">
            <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 font-bold uppercase text-emerald-600 dark:text-emerald-300">
              {couponPreview.code}
            </span>
            <span className="font-semibold text-slate-700 dark:text-slate-200">
              You pay {formatMoney(couponPreview.final, couponPreview.currency)}
            </span>
            <span className="text-slate-400">
              (−{formatMoney(couponPreview.discount, couponPreview.currency)})
            </span>
          </p>
        )}
        {/* Say so when this is not the list price. A buyer comparing the card
            against the public pricing page otherwise has no idea why the numbers
            differ, and no clue which currency they are about to be charged in. */}
        {plan.price_is_localized && !isFree && (
          <p className="mt-0.5 text-[0.6875rem] font-medium text-brand-600 dark:text-brand-300">
            Priced for your country · billed in {plan.currency || BASE_CURRENCY}
          </p>
        )}
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{plan.blurb}</p>
      </div>

      {/* What this plan ALLOWS, straight from the catalog the backend enforces.
          Shown as a labelled table rather than mixed into the tick list, so a
          hard quota is visibly a different kind of thing from a perk. */}
      {Array.isArray(plan.limitLines) && plan.limitLines.length > 0 && (
        <dl className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2 text-xs dark:border-white/10 dark:bg-white/5">
          {plan.limitLines.map((l) => (
            <div key={l.key} className="flex items-baseline justify-between gap-2 py-0.5">
              <dt className="text-slate-500 dark:text-slate-400">{l.label}</dt>
              <dd className="font-semibold text-slate-800 dark:text-slate-100">{l.value}</dd>
            </div>
          ))}
        </dl>
      )}

      <ul className="flex-1 space-y-1.5">
        {(plan.perks || plan.features || []).map((f) => (
          <li key={f} className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-300">
            <CheckCircle2 size={13} className="mt-0.5 shrink-0 text-brand-500" />
            {f}
          </li>
        ))}
      </ul>

      <Button
        size="sm"
        variant={isCurrent ? "outline" : (plan.featured || highlight) ? "primary" : "outline"}
        disabled={isCurrent || anyBusy || freeTrialUnavailable || yearlyUnavailable}
        className="w-full"
        onClick={() => {
          if (isCurrent || freeTrialUnavailable || yearlyUnavailable) return;
          if (isCustom) { if (onContactUs) onContactUs(plan); return; }
          onSelect(plan);
        }}
        href={isCustom && !onContactUs ? "mailto:hello@aiqa.dev" : undefined}
      >
        {isBusy ? <Loader2 size={14} className="animate-spin" /> : null}
        {isBusy ? "Opening checkout…"
          : isCurrent ? "Current Plan"
          : freeTrialUnavailable ? "Not available"
          : yearlyUnavailable ? "Monthly only"
          : plan.cta}
        {!isBusy && !isCurrent && !isCustom && !isFree && !yearlyUnavailable && <ArrowUpRight size={13} />}
      </Button>
    </div>
  );
}
